import { useMemo, useState } from 'react';
import {
  BREAK_KINDS,
  EVENT_LABELS,
  EVENT_TYPES,
  fmtDate,
  fmtTime,
  localDate,
  localToUtc,
  REASON_CODES,
  REASON_LABELS,
  type BreakKind,
  type CorrectionChange,
  type CorrectionDTO,
  type EventDTO,
  type EventType,
  type ReasonCode,
} from '@em/shared';
import { api, invalidateAttendance, useEmployees, useEvents, useJobs, useSettings } from '../api';
import { useSession } from '../auth/AuthContext';
import { localHHMM, todayIn } from './format';
import { Modal } from './Modal';
import { useToast } from './Toast';
import { Alert, Badge, Button, ErrorAlert, Field, Input, Loading, Select, Textarea } from './ui';

type Draft =
  | { key: number; action: 'add'; type: EventType; date: string; time: string; breakKind: BreakKind; jobId: string }
  | { key: number; action: 'change'; eventId: string; date: string; time: string }
  | { key: number; action: 'void'; eventId: string };

export interface CorrectionPrefill {
  employeeId?: string;
  date?: string;
  /** Events the triggering exception refers to; highlighted in the list. */
  eventIds?: string[];
  /** Zone to read and enter times in (timesheet zone); defaults to the business zone. */
  timezone?: string;
  /** Suggested reason, e.g. missed punch. */
  reasonCode?: ReasonCode;
}

let nextKey = 1;

export function CorrectionDialog({ prefill = {}, onClose, onCreated }: { prefill?: CorrectionPrefill; onClose: () => void; onCreated?: (c: CorrectionDTO) => void }) {
  const session = useSession();
  const toast = useToast();
  const settings = useSettings();
  const tz = prefill.timezone ?? settings.data?.defaultTimezone ?? 'Australia/Sydney';
  const employees = useEmployees({ status: 'all' });
  const jobs = useJobs(session.features.jobCosting);

  const [employeeId, setEmployeeId] = useState(prefill.employeeId ?? '');
  const [date, setDate] = useState(prefill.date ?? todayIn(tz));
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [reason, setReason] = useState<ReasonCode>(prefill.reasonCode ?? 'missed_punch');
  const [note, setNote] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const events = useEvents({ employeeId, from: date, to: date }, !!employeeId && !!date);
  const dayEvents = events.data ?? [];
  const byId = useMemo(() => new Map(dayEvents.map((e) => [e.id, e])), [dayEvents]);
  const touched = new Set(drafts.flatMap((d) => (d.action === 'add' ? [] : [d.eventId])));

  const reset = () => setDrafts([]);
  const addPunch = () =>
    setDrafts((d) => [...d, { key: nextKey++, action: 'add', type: dayEvents.some((e) => e.status === 'active' && e.type === 'clock_in') ? 'clock_out' : 'clock_in', date, time: '', breakKind: 'meal', jobId: '' }]);
  const changeEvent = (ev: EventDTO) =>
    setDrafts((d) => [...d, { key: nextKey++, action: 'change', eventId: ev.id, date: localDate(ev.occurredAt, tz), time: localHHMM(ev.occurredAt, tz) }]);
  const voidEvent = (ev: EventDTO) => setDrafts((d) => [...d, { key: nextKey++, action: 'void', eventId: ev.id }]);
  const update = (key: number, patch: Partial<Draft>) => setDrafts((d) => d.map((x) => (x.key === key ? ({ ...x, ...patch } as Draft) : x)));
  const remove = (key: number) => setDrafts((d) => d.filter((x) => x.key !== key));

  const incomplete = drafts.some((d) => d.action !== 'void' && (!d.time || !d.date));
  const noteRequired = reason === 'other';
  const canSubmit = !!employeeId && drafts.length > 0 && !incomplete && (!noteRequired || note.trim().length > 0);

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      const changes: CorrectionChange[] = drafts.map((d) => {
        if (d.action === 'add')
          return {
            action: 'add',
            type: d.type,
            occurredAt: localToUtc(d.date, d.time, tz),
            breakKind: d.type === 'break_start' ? d.breakKind : null,
            jobId: d.jobId || null,
          };
        if (d.action === 'change') return { action: 'change', eventId: d.eventId, occurredAt: localToUtc(d.date, d.time, tz) };
        return { action: 'void', eventId: d.eventId };
      });
      const c = await api.createCorrection({ employeeId, shiftDate: date, changes, reasonCode: reason, note: note.trim() || null });
      invalidateAttendance();
      toast.push({
        kind: 'success',
        title: 'Correction submitted',
        body: 'It is waiting for approval. Someone other than you must approve it; you cannot approve a correction you requested.',
      });
      onCreated?.(c);
      onClose();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  const evLabel = (id: string) => {
    const ev = byId.get(id);
    return ev ? `${EVENT_LABELS[ev.type]} at ${fmtTime(ev.occurredAt, tz)}` : 'event';
  };

  return (
    <Modal
      title="Correct punches"
      wide
      onClose={onClose}
      onSubmit={submit}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" loading={busy} disabled={!canSubmit}>
            Submit for approval
          </Button>
        </>
      }
    >
      <div className="stack">
        <ErrorAlert error={error} />
        <div className="form-grid">
          <Field label="Employee" required>
            <Select
              value={employeeId}
              onChange={(e) => {
                setEmployeeId(e.target.value);
                reset();
              }}
              required
            >
              <option value="">Choose an employee</option>
              {employees.data?.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.displayName} ({e.employeeNumber})
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Shift date" required hint={date ? fmtDate(date) : undefined}>
            <Input
              type="date"
              value={date}
              onChange={(e) => {
                setDate(e.target.value);
                reset();
              }}
              required
            />
          </Field>
        </div>

        <div>
          <h3 style={{ marginBottom: 6 }}>Punches on this day</h3>
          <p className="small muted">Times are in {tz.replace('Australia/', '')} time. Changing or voiding keeps the original record, struck through, with your reason.</p>
          {!employeeId ? (
            <div className="muted small">Choose an employee to see their punches.</div>
          ) : events.isPending ? (
            <Loading />
          ) : events.isError ? (
            <ErrorAlert error={events.error} />
          ) : dayEvents.length === 0 ? (
            <div className="alert">No punches recorded on this day. Add the missing punches below.</div>
          ) : (
            <div className="table-wrap card">
              <table className="table compact">
                <thead>
                  <tr>
                    <th>Punch</th>
                    <th>Time</th>
                    <th>Source</th>
                    <th>Status</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {dayEvents.map((ev) => (
                    <tr key={ev.id} className={`${ev.status !== 'active' ? 'struck' : ''} ${prefill.eventIds?.includes(ev.id) ? 'highlight' : ''}`}>
                      <td className="strike">{EVENT_LABELS[ev.type]}{ev.breakKind ? ` (${ev.breakKind})` : ''}</td>
                      <td className="strike nowrap">{fmtTime(ev.occurredAt, tz)}</td>
                      <td>{ev.source}</td>
                      <td>{ev.status === 'active' ? <Badge tone="green">Active</Badge> : <Badge>{ev.status}</Badge>}</td>
                      <td className="actions">
                        {ev.status === 'active' && (
                          <>
                            <Button size="sm" disabled={touched.has(ev.id)} onClick={() => changeEvent(ev)}>
                              Change time
                            </Button>
                            <Button size="sm" variant="danger" disabled={touched.has(ev.id)} onClick={() => voidEvent(ev)}>
                              Void
                            </Button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div>
          <div className="row between" style={{ marginBottom: 6 }}>
            <h3>Changes in this correction</h3>
            <Button size="sm" onClick={addPunch} disabled={!employeeId}>
              + Add a punch
            </Button>
          </div>
          {drafts.length === 0 && <div className="muted small">No changes yet. Add a punch, or change or void one above.</div>}
          {drafts.map((d) => (
            <div key={d.key} className="change-item">
              {d.action === 'add' && (
                <span className="row">
                  <strong>Add</strong>
                  <Select className="sm" style={{ width: 140 }} aria-label="Punch type" value={d.type} onChange={(e) => update(d.key, { type: e.target.value as EventType })}>
                    {EVENT_TYPES.filter((t) => t !== 'job_switch' || session.features.jobCosting).map((t) => (
                      <option key={t} value={t}>
                        {EVENT_LABELS[t]}
                      </option>
                    ))}
                  </Select>
                  <Input className="sm" style={{ width: 150 }} type="date" aria-label="Punch date" value={d.date} onChange={(e) => update(d.key, { date: e.target.value })} />
                  <Input className="sm" style={{ width: 110 }} type="time" aria-label="Punch time" value={d.time} onChange={(e) => update(d.key, { time: e.target.value })} required />
                  {d.type === 'break_start' && (
                    <Select className="sm" style={{ width: 100 }} aria-label="Break kind" value={d.breakKind} onChange={(e) => update(d.key, { breakKind: e.target.value as BreakKind })}>
                      {BREAK_KINDS.map((k) => (
                        <option key={k} value={k}>
                          {k === 'meal' ? 'Meal' : 'Rest'}
                        </option>
                      ))}
                    </Select>
                  )}
                  {session.features.jobCosting && (d.type === 'clock_in' || d.type === 'job_switch') && (
                    <Select className="sm" style={{ width: 160 }} aria-label="Job" value={d.jobId} onChange={(e) => update(d.key, { jobId: e.target.value })}>
                      <option value="">No job</option>
                      {jobs.data?.filter((j) => j.status === 'active').map((j) => (
                        <option key={j.id} value={j.id}>
                          {j.code} {j.name}
                        </option>
                      ))}
                    </Select>
                  )}
                </span>
              )}
              {d.action === 'change' && (
                <span className="row">
                  <strong>Change</strong> {evLabel(d.eventId)} to
                  <Input className="sm" style={{ width: 150 }} type="date" aria-label="New date" value={d.date} onChange={(e) => update(d.key, { date: e.target.value })} />
                  <Input className="sm" style={{ width: 110 }} type="time" aria-label="New time" value={d.time} onChange={(e) => update(d.key, { time: e.target.value })} required />
                </span>
              )}
              {d.action === 'void' && (
                <span>
                  <strong>Void</strong> {evLabel(d.eventId)}
                </span>
              )}
              <Button size="sm" variant="ghost" onClick={() => remove(d.key)} aria-label="Remove this change">
                Remove
              </Button>
            </div>
          ))}
        </div>

        <div className="form-grid">
          <Field label="Reason" required>
            <Select value={reason} onChange={(e) => setReason(e.target.value as ReasonCode)}>
              {REASON_CODES.map((r) => (
                <option key={r} value={r}>
                  {REASON_LABELS[r]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Note" required={noteRequired} hint={noteRequired ? 'Required when the reason is Other' : 'Optional; kept with the audit record'}>
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} style={{ minHeight: 34 }} />
          </Field>
        </div>
        {incomplete && <Alert tone="amber">Enter a time for every added or changed punch.</Alert>}
      </div>
    </Modal>
  );
}
